import React, { useState } from 'react'
import { Form } from 'antd'
import { useLingui } from '@lingui/react/macro'

import { useCoverStore } from '../../../../_store/useCoverStore'

import type { EditAudioModalData } from '@/pages/tad.ai/_components/EditAudioModal'
import type { FileFormValue } from '@/pages/tad.ai/_components/UploadAudio'
import type { FormInstance } from 'antd'
import type { CoverFormValues } from '../../../../_types/form'

import SharedEditAudioModal from '@/pages/tad.ai/_components/EditAudioModal'
import SharedHistoryModal from '@/pages/tad.ai/_components/HistoryModal'
import UploadAudioCard from '@/pages/tad.ai/_components/UploadAudio/UploadAudioCard'
import AudioCard from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/components/MusicStyle/_components/ReferenceForm/_components/AudioCard'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { hoverBorderCls } from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/components/MusicStyle/_components/ReferenceForm/_components/ReferenceMusicCard/constants'
import MenuCard from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/components/MusicStyle/_components/ReferenceForm/_components/MenuCard'
import { useReferenceUpload } from '@/pages/tad.ai/_components/UploadAudio/_hooks/useReferenceUpload'


import { cls } from 'tagged-classnames-free';
interface CoverReferenceAudioFieldProps {
  form: FormInstance<CoverFormValues>
  onCancelDetecting?: () => void
  onDetectedLyrics?: (lyrics: string) => void
  onDraftChange?: () => void
}

const COVER_MAX_DURATION = 480 // 8 minutes
const COVER_MIN_DURATION = 30
const COVER_MAX_UPLOAD_SIZE = 200 // MB
const COVER_SUPPORT_AUDIO = ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac']

const EMPTY_EDIT_AUDIO_DATA: EditAudioModalData = {
  musicModel: 'V3',
  musicType: 'Cover',
  filename: '',
  file: '',
  maxDuration: COVER_MAX_DURATION,
  minDuration: COVER_MIN_DURATION,
}

function CoverReferenceAudioField(props: CoverReferenceAudioFieldProps) {
  const { form, onCancelDetecting, onDetectedLyrics, onDraftChange } = props
  const { t } = useLingui()
  const value = Form.useWatch('referenceAudio', form)
  const mode = Form.useWatch('mode', form)

  // ─── 从 store 读写上传状态 ───
  const isUploading = useCoverStore((s) => s.ui.isUploading)
  const setUI = useCoverStore((s) => s.setUI)

  const [historyOpen, setHistoryOpen] = useState(false)
  const [editAudioData, setEditAudioData] = useState<EditAudioModalData>(
    EMPTY_EDIT_AUDIO_DATA,
  )
  const [editOpen, setEditOpen] = useState(false)

  const { isSignIn, sessionLoading } = useSessionInfo()
  const { signUp } = useAppUIModal()

  const { handleReferencePolling } = useReferenceUpload({
    setUploading: (uploading: boolean) => setUI({ isUploading: uploading }),
    onDetectedLyrics,
    onSuccess: () => {},
  })

  const setReferenceAudio = (file?: FileFormValue) => {
    form.setFieldValue(
      'referenceAudio',
      file
        ? {
            token: file.token,
            filename: file.filename,
          }
        : null,
    )
    onDraftChange?.()
  }

  const handleUploadGate = (e: React.MouseEvent) => {
    if (sessionLoading || isUploading) {
      e.stopPropagation()
      e.preventDefault()
      return
    }

    if (!isSignIn) {
      e.stopPropagation()
      e.preventDefault()
      signUp.show({ defaultShowSignUp: true })
    }
  }

  const openEditAudioModal = (
    data: Omit<EditAudioModalData, 'musicModel' | 'musicType'> &
      Partial<Pick<EditAudioModalData, 'musicModel' | 'musicType'>> & {
        onEdited?: (file: FileFormValue) => void
      },
  ) => {
    const { onEdited, ...editData } = data

    setEditAudioData({
      ...EMPTY_EDIT_AUDIO_DATA,
      ...editData,
      musicModel: 'V3',
      musicType: 'Cover',
      onDetectedLyrics,
      isNeedDetectLyrics: mode === 'custom',
      onSuccess: ({ token, filename }) => {
        const nextFile = { token: token || '', filename }
        setReferenceAudio(nextFile)
        onEdited?.(nextFile)
        setEditOpen(false)
      },
    })
    setEditOpen(true)
  }

  const handleOpenHistory = () => {
    if (!isSignIn) {
      signUp.show({ defaultShowSignUp: true })
      return
    }
    setHistoryOpen(true)
  }

  return (
    <>
      <Form.Item
        name='referenceAudio'
        label={t`Reference Audio`}
        validateTrigger='onSubmit'
        rules={[
          {
            validator(_, value) {
              if (!value?.token) {
                return Promise.reject(
                  new Error(t`Please upload an audio file to get started.`),
                )
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <div className={cls`space-y-2`}>
          {value?.token ? (
            <AudioCard
              value={value || undefined}
              handleHistory={handleOpenHistory}
              disabled={isUploading}
            />
          ) : null}

          <div className={cls`flex items-center gap-2`}>
            <div
              className={cls`grid flex-1 grid-cols-1 gap-x-2 ${hoverBorderCls} rounded-[10px]`}
              onClickCapture={handleUploadGate}
            >
              <UploadAudioCard
                acceptList={COVER_SUPPORT_AUDIO}
                maxUploadSize={COVER_MAX_UPLOAD_SIZE}
                onBeforeUpload={() => {
                  setUI({ isUploading: false })
                }}
                onUploadError={() => {
                  setUI({ isUploading: false })
                }}
                onUploading={() => {
                  setUI({ isUploading: true })
                  if (mode === 'custom') {
                    setUI({ isDetecting: true })
                  }
                }}
                onUploadSuccess={({ url, filename }) => {
                  setUI({ isUploading: false })
                  openEditAudioModal({
                    filename,
                    file: url,
                  })
                }}
              >
                <MenuCard
                  icon={cls`i-cus--tad-upload-02`}
                  label={t`Upload Audio`}
                  loading={isUploading}
                />
              </UploadAudioCard>
            </div>

            <MenuCard
              icon='i-com--history'
              onClick={handleOpenHistory}
              className={cls`${hoverBorderCls}`}
              disabled={isUploading}
            />
          </div>
        </div>
      </Form.Item>

      <SharedHistoryModal
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false)
        }}
        musicModel='V3'
        musicType='Cover'
        onUse={async (file) => {
          setReferenceAudio(file)
          if (mode === 'custom' && onDetectedLyrics && file.token) {
            setUI({ isDetecting: true })
            await handleReferencePolling(file.token)
          }
        }}
        onEditAudio={(data) => {
          setHistoryOpen(false)
          openEditAudioModal({
            ...data,
            onEdited: () => {
              data.onSuccess?.()
            },
          })
        }}
      />

      <SharedEditAudioModal
        open={editOpen}
        data={editAudioData}
        onClose={() => {
          setEditOpen(false)
          onCancelDetecting?.()
        }}
      />
    </>
  )
}

export default CoverReferenceAudioField
